<template>
  <v-main>
    <v-container>
      <Page gridType="centeredContent">
        <template v-slot:headerInfo>
          <div v-if="!loadingApp && !errorLoadingApp">
            <NtBreadcrumb :breadcrumbItems="items" />
            <v-row>
              <v-col cols="12" md="8">
                <div
                  class="d-flex flex-row justify-start align-center flex-nowrap"
                >
                  <v-avatar size="100" class="mr-5" rounded>
                    <v-img :src="app.icon.url"> </v-img>
                  </v-avatar>
                  <div>
                    <h2>{{ app.name }}</h2>
                    <div
                      class="d-flex flex-row justify-start align-baseline flex-wrap"
                    >
                      <p class="body-regular mb-0 mr-5">
                        {{ app.publisher.name }}
                      </p>
                      <p class="body-regular text-secondary mb-0">
                        {{ app.app_category.name }}
                      </p>
                    </div>
                    <div
                      class="d-flex flex-row flex-wrap justify-start align-center"
                    >
                      <NtChip
                        v-for="targetDevice in app.target_devices"
                        :key="targetDevice.id"
                        :chipName="targetDevice.deviceName"
                        :label="true"
                        :close="false"
                        size="small"
                      />
                    </div>
                  </div>
                </div>
              </v-col>
              <v-col
                cols="12"
                md="4"
                class="d-flex flex-row justify-start justify-sm-end align-center"
              >
                <v-menu offset-y dense>
                  <template v-slot:activator="{ on }">
                    <NtButton
                      type="secondary"
                      size="small"
                      value="Share"
                      class="mr-3"
                      icon="fas fa-share-square"
                      iconPosition="left"
                      v-on="on"
                    />
                  </template>
                  <v-list>
                    <v-list-item link @click="shareEmail"
                      >Share via email</v-list-item
                    >
                    <v-list-item link @click="copyLink" v-clipboard="linkToCopy"
                      >Copy link</v-list-item
                    >
                  </v-list>
                </v-menu>
                <NtButton
                  type="secondary"
                  size="small"
                  value="Request information"
                  icon="fas fa-info-circle"
                  iconPosition="left"
                  @click="userRequestedInfo"
                />
              </v-col>
            </v-row>
          </div>
          <div v-if="loadingApp">
            <div class="d-flex flex-row justify-start align-center mb-6">
              <v-skeleton-loader
                width="100"
                type="chip"
                class="mr-1 flex-grow-0"
              ></v-skeleton-loader>
              <v-skeleton-loader
                width="100"
                type="chip"
                class="mr-1 flex-grow-0"
              ></v-skeleton-loader>
            </div>
            <v-row>
              <v-col
                cols="12"
                md="8"
                class="d-flex flex-row justify-start align-center flex-nowrap"
              >
                <v-skeleton-loader
                  class="mr-3"
                  height="100"
                  width="100"
                  type="image"
                ></v-skeleton-loader>
                <div>
                  <v-skeleton-loader
                    min-width="300"
                    type="heading"
                  ></v-skeleton-loader>
                  <v-skeleton-loader
                    min-width="150"
                    type="text"
                    class="mt-4"
                  ></v-skeleton-loader>
                  <div
                    class="d-flex flex-row align-center justify-start flex-wrap"
                  >
                    <v-skeleton-loader
                      class="mr-1"
                      min-width="60"
                      type="chip"
                    ></v-skeleton-loader>
                    <v-skeleton-loader
                      class="mr-1"
                      min-width="60"
                      type="chip"
                    ></v-skeleton-loader>
                    <v-skeleton-loader
                      class="mr-1"
                      min-width="60"
                      type="chip"
                    ></v-skeleton-loader>
                  </div>
                </div>
              </v-col>
              <v-col
                cols="12"
                md="4"
                class="d-flex flex-row justify-start justify-lg-end align-center"
              >
                <v-skeleton-loader
                  class="mx-auto mr-3"
                  max-width="300"
                  type="button"
                ></v-skeleton-loader>
                <v-skeleton-loader
                  class="mx-auto"
                  max-width="300"
                  type="button"
                ></v-skeleton-loader>
              </v-col>
            </v-row>
          </div>
        </template>
        <template v-slot:singleSection>
          <div v-if="loadingApp">
            <div
              class="d-flex flex-row overflow-x align-start justify-start mb-3 pb-3"
            >
              <v-skeleton-loader
                v-for="n in 6"
                :key="n"
                min-width="200"
                max-height="350"
                type="image"
                class="mr-4 flex-grow-0 flex-shrink-0"
              ></v-skeleton-loader>
            </div>
            <v-skeleton-loader type="article" class="mt-5"></v-skeleton-loader>
            <v-skeleton-loader type="article" class="mt-5"></v-skeleton-loader>
          </div>
          <div v-if="!loadingApp && !errorLoadingApp">
            <div
              v-if="app.media"
              class="d-flex flex-row overflow-x align-start justify-start mb-8 pb-3"
            >
              <img
                v-for="image in app.media"
                :key="image.id"
                :src="image.url"
                height="350"
                class="flex-shrink-0 flex-grow-0 ma-3"
              />
            </div>
            <h4 class="subtitle text-secondary mb-2 mt-5">About the App</h4>
            <div :class="collapsed ? 'txtCollapsed' : ''" v-html="app.description"></div>
            <NtButton
              type="terciary"
              :value="collapsed ? 'Show more' : 'Hide'"
              :block="true"
              @click="toggleCollapse"
              size="small"
            />

            <div v-if="app.app_releases && app.app_releases.length > 0">
              <h4 class="subtitle text-secondary mb-2 mt-9">Release Notes</h4>
              <div class="d-flex flex-row justify-start align-start">
                <p class="caption text-primary mb-0 mr-2">
                  <b>version {{ latestRelease.version }}</b>
                </p>
                <p class="caption text-secondary mb-0">
                  <b>{{ latestRelease.releaseDate }}</b>
                </p>
              </div>
              <p class="body-regular text-primary">
                {{ latestRelease.notes }}
              </p>
            </div>

            <h4 class="subtitle text-secondary mb-2 mt-9">
              Additional Information
            </h4>
            <v-row>
              <v-col cols="6" md="4" v-if="app.privacyPolicyURL">
                <a
                  :href="app.privacyPolicyURL"
                  class="d-flex flex-row align-center justify-start"
                >
                  Privacy Policy
                  <v-icon size="11" class="ml-2"
                    >fas fa-external-link-alt</v-icon
                  >
                </a>
              </v-col>
              <v-col cols="6" md="4" v-if="app.publisher.website">
                <a
                  :href="app.publisher.website"
                  class="d-flex flex-row align-center justify-start"
                >
                  Publisher's website
                  <v-icon size="11" class="ml-2"
                    >fas fa-external-link-alt</v-icon
                  >
                </a>
              </v-col>
            </v-row>
          </div>
          <div
            v-else-if="!loadingApp && errorLoadingApp"
            class="d-flex flex-row align-center justify-start"
          >
            <v-icon size="20" class="mr-2">fas fa-info-circle</v-icon>
            <p class="ma-0">
              Something went wrong loading the app. Please refresh your browser
              or try again later.
            </p>
          </div>
        </template>
        <template v-slot:additionalSpace>
          <NtDialog
            :showDialog="infoRequestDialog"
            title="Request Information"
            @close="cancel"
          >
            <template v-slot:dialogContent>
              <v-window v-model="step">
                <!-- <v-window-item :value="1" class="pa-2">
                  <p class="body-small text-primary">
                    If you already have an account, sign in to request
                    information. You can also request information as a guest.
                  </p>
                </v-window-item> -->

                <v-window-item :value="1" class="pa-2">
                  <p class="body-small text-primary">
                    Please provide the following data so we can contact you.
                  </p>
                  <NtInputText
                    label="Name"
                    :required="true"
                    type="text"
                    v-model="requestor.name"
                    class="mb-5"
                  />
                  <NtInputText
                    label="Email"
                    :required="true"
                    :type="
                      (requestResponse && requestResponse.includes('email')) ||
                      !emailIsCorrect
                        ? 'error'
                        : ''
                    "
                    v-model="requestor.email"
                    :class="!emailIsCorrect ? '' : 'mb-5'"
                    @blur="checkEmail"
                  />
                  <span
                    v-if="
                      !emailIsCorrect ||
                        (requestResponse && requestResponse.includes('email'))
                    "
                    class="utility error-text"
                    >Enter a valid email address</span
                  >
                  <NtInputText
                    label="Phone number"
                    :required="true"
                    :type="!phoneIsCorrect ? 'error' : 'primary'"
                    nature="tel"
                    v-model="requestor.phone"
                    :class="!emailIsCorrect ? 'mt-4' : ''"
                    @blur="checkPhone"
                  />
                  <span
                    :class="
                      !phoneIsCorrect
                        ? 'utility error-text'
                        : 'utility text-secondary'
                    "
                    >{{
                      !phoneIsCorrect
                        ? "Enter a valid phone number"
                        : "+00 000 000 0000"
                    }}</span
                  >
                  <NtInputText
                    label="Company Name"
                    :required="false"
                    type="text"
                    v-model="requestor.companyName"
                    class="mb-4 mt-4"
                  />
                  <NtDropdown
                    :dataSource="regions"
                    mapValue="id"
                    displayText="name"
                    :disabled="false"
                    :description="false"
                    :required="true"
                    :loading="loadingRegions"
                    v-model="requestor.region"
                    label="Region"
                  />
                  <!-- form -->
                </v-window-item>

                <v-window-item :value="3" class="pa-2">
                  <div
                    class="d-flex flex-column align-center justify-center mb-5"
                    v-if="submitting"
                  >
                    <v-progress-circular
                      indeterminate
                      size="70"
                      color="#9D9D9D"
                      width="2"
                    ></v-progress-circular>
                    <p class="body-small text-secondary mt-2">
                      {{ supportingMessage }}
                    </p>
                  </div>
                  <p class="body-small text-primary" v-else>
                    Your information request has been submitted successfully.
                    Soon, a sales representative will get in touch with you.
                  </p>
                  <!-- confirmation -->
                </v-window-item>
              </v-window>
              <!-- <v-card-actions class="d-flex flex-row pa-2" v-if="step === 0">
                <div class="d-md-none d-flex flex-column flex-grow-1">
                  <NtButton
                    size="big"
                    type="primary"
                    value="Sign in"
                    class="mb-3"
                    @click="signInHere"
                    :block="true"
                  />
                  <NtButton
                    size="big"
                    type="terciary"
                    value="Continue as a guest"
                    @click="continueAsGuest"
                    :block="true"
                  />
                </div>
                <div class="d-none d-md-flex flex-grow-1 flex-row justify-end">
                  <NtButton
                    size="regular"
                    type="terciary"
                    value="Continue as a guest"
                    @click="continueAsGuest"
                    class="mr-2"
                  />
                  <NtButton
                    size="regular"
                    type="primary"
                    value="Sign in"
                    @click="signInHere"
                  />
                </div>
              </v-card-actions> -->
              <v-card-actions
                class="d-flex flex-row pa-2 pt-8"
                v-if="step === 1"
              >
                <div class="d-flex flex-grow-1 flex-row justify-end">
                  <NtButton
                    size="regular"
                    type="terciary"
                    value="Cancel"
                    @click="cancel"
                    class="mr-2"
                  />
                  <vue-recaptcha
                    ref="recaptcha"
                    size="invisible"
                    @verify="submitClicked"
                    @expired="onCaptchaExpired"
                    :loadRecaptchaScript="true"
                    sitekey="6LcIibcZAAAAAMx3JCwaCmN34qgh4UADXimIt4br"
                  >
                  </vue-recaptcha>
                  <NtTooltip
                    v-if="!canSubmit"
                    position="top"
                    value="There are missing fields that need to be entered"
                  >
                    <template v-slot:element>
                      <NtButton
                        size="regular"
                        type="primary"
                        value="Submit"
                        :disabled="!canSubmit"
                        @click="checkCaptcha"
                      />
                    </template>
                  </NtTooltip>
                  <NtButton
                    v-else
                    size="regular"
                    type="primary"
                    value="Submit"
                    :disabled="!canSubmit"
                    @click="checkCaptcha"
                  />
                </div>
              </v-card-actions>
              <v-card-actions
                class="d-flex flex-row pa-2"
                v-else-if="step === 3 && !submitting"
              >
                <div class="d-md-none d-flex flex-column flex-grow-1">
                  <NtButton
                    size="big"
                    type="primary"
                    value="Okay"
                    class="mb-3"
                    @click="cancel"
                    :block="true"
                  />
                </div>
                <div class="d-none d-md-flex flex-grow-1 flex-row justify-end">
                  <NtButton
                    size="regular"
                    type="primary"
                    value="Okay"
                    @click="cancel"
                  />
                </div>
              </v-card-actions>
            </template>
          </NtDialog>
          <NtAlert
            :type="alert.type"
            :title="alert.title"
            :value="alert.value"
            @close="alert.value = false"
          />
          <FullLoader
            v-if="loadingApp"
            loadingMessage="We're loading your content"
          />
          <NtAlert
            type="error"
            :title="requestResponse"
            :value="requestResponse !== null"
            @close="clearReqResponse"
          />
        </template>
      </Page>
    </v-container>
  </v-main>
</template>

<script>
import Page from "./shared/Page.vue";
import { mapState, mapActions } from "vuex";
import FullLoader from "./shared/FullLoader.vue";
import moment from "moment";
import VueRecaptcha from "vue-recaptcha";

export default {
  name: "appDetails",
  components: {
    Page,
    FullLoader,
    VueRecaptcha,
  },
  props: ["id"],
  data() {
    return {
      items: [
        {
          text: "All apps",
          disabled: false,
          to: "/",
        },
        {
          text: "App details",
          disabled: true,
          href: "breadcrumbs_link_2",
        },
      ],
      alert: {
        type: "",
        title: "",
        value: false,
      },
      step: 1,
      collapsed: true,
      infoRequestDialog: false,
      submitting: true,
      supportingMessage: "",
      emailIsCorrect: true,
      phoneIsCorrect: true,
      requestor: {
        name: "",
        email: "",
        phone: "",
        companyName: "",
        region: "",
      },
    };
  },

  created() {
    this.getApp({ id: this.id });
    if (window.localStorage.getItem("redirectToRequest")) {
      this.infoRequestDialog = true;
      this.step = 3;
      this.supportingMessage = "Waiting for authentication...";
    }
  },

  computed: {
    ...mapState({
      app: (state) => state.singleApp.data,
      loadingApp: (state) => state.singleApp.loadingApp,
      errorLoadingApp: (state) => state.singleApp.errorLoadingApp,
      regions: (state) => state.regions.data,
      loadingRegions: (state) => state.regions.loadingRegions,
      errorLoadingRegions: (state) => state.regions.errorLoadingRegions,
      requestors: (state) => state.infoRequest.requestors,
      salesReps: (state) => state.salesReps.data,
      requestResponse: (state) => state.infoRequest.requestResponse,
      currentUser: (state) => state.currentUser.data,
    }),
    linkToCopy() {
      return window.location.href;
    },
    canSubmit() {
      return (
        this.requestor.name !== "" &&
        this.requestor.email !== "" &&
        this.requestor.email.includes("@") &&
        this.requestor.email.includes(".") &&
        this.requestor.phone !== "" &&
        this.requestor.phone.length >= 10 &&
        this.requestor.region !== ""
      );
    },
  },

  methods: {
    ...mapActions({
      getApp: "singleApp/getApp",
      getRegions: "regions/getRegions",
      getRequestors: "infoRequest/getRequestors",
      addRequestor: "infoRequest/addRequestor",
      submitRequest: "infoRequest/submitRequest",
      getSalesReps: "salesReps/getSalesReps",
      clearRequestResponse: "infoRequest/clearRequestResponse",
    }),
    checkCaptcha() {
      this.$refs.recaptcha.execute();
    },
    onCaptchaExpired() {
      this.$refs.recaptcha.reset();
    },
    userRequestedInfo() {
      this.infoRequestDialog = true;
      this.getRegions();
      if (this.currentUser) {
        this.step = 3;
        this.requestAsSignedIn();
      } else {
        this.step = 1;
      }
    },
    continueAsGuest() {
      this.step = 2;
      this.getRegions();
    },
    requestAsSignedIn() {
      this.infoRequestDialog = true;
      this.step = 3;
      this.requestor = {
        name: this.currentUser.firstName + " " + this.currentUser.lastName,
        email: this.currentUser.email,
        phone: this.currentUser.phoneNumber,
        companyName: this.currentUser.operators[0].name,
        region: 2,
        isaRegisteredUser: true,
      };
      this.getRequestors()
        .then(() => {
          const res = this.requestors.map(
            (storedRequestor) => storedRequestor.email === this.requestor.email
          )[0];
          if (!res) {
            this.addRequestor(this.requestor)
              .then(() => {
                if (this.requestResponse === null) {
                  this.submitNewRequest();
                }
              })
              .catch((e) => console.log(e));
          } else {
            this.submitNewRequest();
          }
        })
        .catch((e) => console.log(e));

      window.localStorage.removeItem("redirectToRequest");
    },
    getDate(dateSent) {
      if (dateSent) {
        return moment(dateSent, moment.ISO_8601)
          .utc()
          .format("MMM DD, YYYY")
          .toString();
      } else {
        return "Not available";
      }
    },
    cancel() {
      this.infoRequestDialog = false;
      this.supportingMessage = "";
      setTimeout(() => {
        this.step = 1;
      }, 1000);
      this.clearRequestorFields();
    },
    signInHere() {
      this.submitting = true;
      this.supportingMessage = "Waiting for authentication...";
      this.step = 3;
      window.localStorage.setItem("redirectToRequest", window.location.href);
      window.open("/login", "_self");
      setTimeout(() => {
        this.submitting = false;
      }, 5000);
    },
    submitClicked() {
      this.onCaptchaExpired();
      this.submitting = true;
      this.supportingMessage = "Submitting your request...";
      this.step = 3;
      // get requestors
      this.getRequestors()
        .then(() => {
          const res = this.requestors.map(
            (storedRequestor) => storedRequestor.email === this.requestor.email
          )[0];
          if (!res) {
            this.addRequestor(this.requestor)
              .then(() => {
                if (this.requestResponse === null) {
                  this.submitNewRequest();
                } else {
                  this.step = 2;
                }
              })
              .catch((e) => console.log(e));
          } else {
            this.submitNewRequest();
          }
        })
        .catch((e) => console.log(e));
    },
    submitNewRequest() {
      this.getRequestors()
        .then(() => {
          const recentEntry = this.requestors.filter(
            (x) => x.email === this.requestor.email
          )[0];
          this.submitRequest({
            app: this.app.id,
            requestor: recentEntry.id,
            sales_group: this.app.sales_group.id,
          })
            .then(() => {
              this.submitting = false;
              this.step = 3;
              this.clearRequestorFields();
            })
            .catch((e) => console.log(e));
        })
        .catch((e) => console.log(e));
    },
    copyLink() {
      let success = {
        title: "Link successfully copied to clipboard.",
        type: "success",
        value: true,
      };
      this.alert = success;
    },
    shareEmail() {
      let email = {
        subject: "NextNet App: " + this.app.name,
        body:
          "Hey,%0D%0A %0D%0A Check out this app I found on NextNet's Apps Catalog." +
          "%0D%0A %0D%0A %0D%0A" +
          this.app.name +
          "%0D%0A %0D%0A You can find more information here: " +
          window.location.href,
      };
      window.open("mailto:?subject=" + email.subject + "&body=" + email.body);
    },
    clearRequestorFields() {
      this.requestor.name = "";
      this.requestor.email = "";
      this.requestor.phone = "";
      this.requestor.companyName = "";
      this.requestor.region = "";
    },
    clearReqResponse() {
      this.clearRequestResponse();
    },
    checkEmail() {
      if (
        this.requestor.email !== "" &&
        this.requestor.email.includes("@") &&
        this.requestor.email.includes(".")
      ) {
        this.emailIsCorrect = true;
      } else {
        this.emailIsCorrect = false;
      }
    },
    checkPhone() {
      let letters = 0;
      if (this.requestor.phone !== "" && this.requestor.phone.length >= 10) {
        this.phoneIsCorrect = true;
        for (let index = 0; index < this.requestor.phone.length; index++) {
          if (isNaN(this.requestor.phone[index] / 1)) {
            if (
              this.requestor.phone[index] !== " " &&
              this.requestor.phone[index] !== "(" &&
              this.requestor.phone[index] !== "+" &&
              this.requestor.phone[index] !== ")"
            ) {
              letters += 1;
            }
          }
        }
        if (letters >= 1) {
          this.phoneIsCorrect = false;
        } else {
          this.phoneIsCorrect = true;
        }
      } else {
        this.phoneIsCorrect = false;
      }
    },
    toggleCollapse() {
      this.collapsed = !this.collapsed
    }
  },

  watch: {
    app() {
      if (this.app.app_releases && this.app.app_releases.length > 0) {
        this.latestRelease = this.app.app_releases[
          this.app.app_releases.length - 1
        ];
        this.latestRelease.releaseDate = this.getDate(
          this.latestRelease.releaseDate
        );
      }
    },

    requestResponse() {
      if (this.requestResponse !== null) {
        setTimeout(() => {
          this.clearReqResponse();
        }, 3000);
      }
    },

    alert: function() {
      if (this.requestResponse !== null) {
        setTimeout(() => {
          this.alert.value = false;
        }, 3000);
      }
    },

    currentUser() {
      if (window.localStorage.getItem("redirectToRequest")) {
        this.requestAsSignedIn();
      }
    },
  },
};
</script>
<style>
.txtCollapsed {
  max-height: 70px;
  overflow: hidden;
  text-overflow: ellipsis;
}
.error-text {
  color: #c02100 !important;
}
</style>
